import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface PostureIndicator {
  readonly principle: string;
  readonly enabled: boolean;
  readonly basis: string;
}
interface ResponsibleAiPosture {
  readonly humanOversight: boolean;
  readonly humanApprovalRequired: boolean;
  readonly contentApprovalRequired: boolean;
  readonly promptApprovalRequired: boolean;
  readonly responseValidationRequired: boolean;
  readonly biasControls: boolean;
  readonly transparency: boolean;
  readonly explainability: boolean;
  readonly privacyProtection: boolean;
  readonly piiDetection: boolean;
  readonly sensitiveDataProtection: boolean;
  readonly auditability: boolean;
  readonly hallucinationControls: boolean;
  readonly indicators: readonly PostureIndicator[];
}

const POLICY_KEYS = [
  'AI_ENABLED', 'HUMAN_APPROVAL_REQUIRED', 'CONTENT_APPROVAL_REQUIRED', 'PROMPT_APPROVAL_REQUIRED',
  'RESPONSE_VALIDATION_REQUIRED', 'SENSITIVE_DATA_BLOCK', 'PII_DETECTION_ENABLED', 'HALLUCINATION_CONFIDENCE_MIN',
  'APPROVAL_SLA_HOURS', 'RISK_REVIEW_INTERVAL_DAYS', 'ADOPTION_TARGET_PCT', 'AUDIT_RETENTION_YEARS',
];

/**
 * M38 — AI Governance Dashboard (§17/§28). Presents the Responsible-AI posture —
 * the six governed principles (human oversight, bias & fairness, transparency,
 * explainability, privacy, auditability) with the policy basis each derives from
 * — reflecting the live §6 policy parameters. Governance officers record a
 * policy change (GOVERNANCE_MANAGE), which is audited (AI_POLICY_UPDATED) and
 * emitted to the event stream (§26). Tenant isolation is enforced server-side
 * (LAW 4). A 403 surfaces a clear permission message.
 */
export function AiGovernanceDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [posture, setPosture] = useState<ResponsibleAiPosture | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [policyKey, setPolicyKey] = useState(POLICY_KEYS[0]);
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [updated, setUpdated] = useState<{ readonly policyKey: string; readonly updatedAt: string } | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: ResponsibleAiPosture }>(`/v1/ai/governance/posture`, { token })
      .then((r) => { if (alive) { setPosture(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setUpdated(null);
    setBusy(true);
    try {
      const r = await apiRequest<{ data: { policyKey: string; updatedAt: string } }>(`/v1/ai/governance/policy`, {
        method: 'POST',
        body: { policyKey, summary: summary.trim() || undefined },
        token,
      });
      setUpdated(r.data);
      setSummary('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m38.governance.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="ai-governance-dashboard">
      <h1>{t('m38.governance.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !posture ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="responsible-ai-principles">
            <h2>{t('m38.governance.principlesHeading')}</h2>
            <DataTable<PostureIndicator>
              ariaLabel="posture-indicators"
              testId="posture-indicators"
              rowKey={(ind) => ind.principle}
              rowDataAttrs={(ind) => ({ 'data-principle': ind.principle })}
              rows={posture.indicators}
              columns={[
                { key: 'principle', header: t('m38.governance.colPrinciple'), render: (ind) => ind.principle },
                { key: 'enabled', header: t('m38.governance.colEnabled'), cellAriaLabel: (ind) => `principle-enabled-${ind.principle}`, render: (ind) => ind.enabled ? t('common.yes') : t('common.no') },
                { key: 'basis', header: t('m38.governance.colBasis'), render: (ind) => ind.basis },
              ]}
            />
          </section>

          <section aria-label="responsible-ai-controls">
            <h2>{t('m38.governance.controlsHeading')}</h2>
            <dl>
              <dt>{t('m38.governance.humanOversight')}</dt><dd aria-label="control-human-oversight">{posture.humanOversight ? t('m38.governance.on') : t('m38.governance.off')}</dd>
              <dt>{t('m38.governance.contentApproval')}</dt><dd aria-label="control-content-approval">{posture.contentApprovalRequired ? t('m38.governance.on') : t('m38.governance.off')}</dd>
              <dt>{t('m38.governance.responseValidation')}</dt><dd aria-label="control-response-validation">{posture.responseValidationRequired ? t('m38.governance.on') : t('m38.governance.off')}</dd>
              <dt>{t('m38.governance.piiDetection')}</dt><dd aria-label="control-pii">{posture.piiDetection ? t('m38.governance.on') : t('m38.governance.off')}</dd>
              <dt>{t('m38.governance.sensitiveData')}</dt><dd aria-label="control-sensitive">{posture.sensitiveDataProtection ? t('m38.governance.on') : t('m38.governance.off')}</dd>
              <dt>{t('m38.governance.hallucination')}</dt><dd aria-label="control-hallucination">{posture.hallucinationControls ? t('m38.governance.on') : t('m38.governance.off')}</dd>
              <dt>{t('m38.governance.auditability')}</dt><dd aria-label="control-auditability">{posture.auditability ? t('m38.governance.on') : t('m38.governance.off')}</dd>
            </dl>
          </section>

          <section aria-label="update-policy">
            <h2>{t('m38.governance.recordHeading')}</h2>
            <form aria-label="policy-form" onSubmit={(e) => void submit(e)}>
              <label>{t('m38.governance.policyKey')}
                <select aria-label="field-policy-key" value={policyKey} onChange={(e) => setPolicyKey(e.target.value)}>
                  {POLICY_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </label>
              <label>{t('m38.governance.summary')}
                <input aria-label="field-summary" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder={t('m38.governance.summaryPh')} />
              </label>
              <Button variant="primary" type="submit" aria-label="submit-policy" disabled={busy}>{busy ? t('m38.common.saving') : t('m38.governance.recordBtn')}</Button>
            </form>
            {actionError && <p role="alert" aria-label="policy-error">{actionError}</p>}
            {updated && <p aria-label="policy-updated">{t('m38.governance.updated', { policyKey: updated.policyKey, when: at(updated.updatedAt) })}</p>}
          </section>
        </>
      )}
    </main>
  );
}
